/**
 * Login Form Component with Email/Password and Google Sign-In
 * Uses shadcn/ui components for a clean, elegant design
 *
 * When `oauthParams` is provided, the form operates in OAuth mode:
 * after successful Firebase sign-in, it POSTs the ID token to the
 * MCP server's /authorize/callback and redirects to the returned URL.
 * A browser already signed in is asked first which account to connect.
 * The server says where the code would go: Claude's apps and the user's own machine keep the one-click flow; any other
 * host gets a warning naming it, and nothing is posted until the user confirms they started there.
 */

import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  signInWithEmailPassword,
  signUpWithEmailPassword,
  sendPasswordReset,
  signOutUser,
  subscribeToAuthState,
  getCurrentUserToken,
} from '../../services/firebase';
import type { User } from 'firebase/auth';
import { projectForEmail, projectForSignUp, signInWithGoogleOnAnyProject } from './authProject';
import { isAllowedOAuthCallback, lookupDestination, type DestinationAnswer } from './oauthCallback';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { Checkbox } from '../ui/checkbox';

export interface OAuthParams {
  oauthCallback: string;
  clientId: string;
  codeChallenge: string;
  redirectUri: string;
  state?: string;
}

type AuthMode = 'signin' | 'signup' | 'reset';

interface LoginFormProps {
  oauthParams?: OAuthParams | null;
  initialMode?: AuthMode;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * After Firebase sign-in, POST the ID token to the MCP server's /authorize/callback.
 * The server verifies it, creates an auth code, and returns a redirect URL.
 */
const FOREIGN_CALLBACK = 'This sign-in link did not come from 4K Labs, so nothing was sent. Start the connection again from Claude.';

async function handleOAuthCallback(oauthParams: OAuthParams): Promise<void> {
  if (!isAllowedOAuthCallback(oauthParams.oauthCallback)) throw new Error(FOREIGN_CALLBACK);
  const idToken = await getCurrentUserToken();
  if (!idToken) throw new Error('No authenticated user');

  const response = await fetch(oauthParams.oauthCallback, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_token: idToken,
      client_id: oauthParams.clientId,
      code_challenge: oauthParams.codeChallenge,
      redirect_uri: oauthParams.redirectUri,
      state: oauthParams.state,
    }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Authorization failed' }));
    throw new Error(body.error || 'Authorization failed');
  }

  const { redirectUrl } = await response.json();
  window.location.href = redirectUrl;
}

export const LoginForm: React.FC<LoginFormProps> = ({ oauthParams, initialMode = 'signin', onSuccess, onError }) => {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('reason') === 'signin-moved'
      ? 'Your sign-in moved. Sign in again to continue.'
      : null
  );

  const [signedIn, setSignedIn] = useState<User | null | undefined>(oauthParams ? undefined : null);
  const [answer, setAnswer] = useState<DestinationAnswer | undefined>(undefined);
  const [confirmed, setConfirmed] = useState(false);

  const isLoading = loading || googleLoading;
  const isOAuthMode = !!oauthParams;
  const destination = answer && !answer.refused ? answer.destination : null;

  useEffect(() => {
    if (!oauthParams) return;
    return subscribeToAuthState((user) => setSignedIn((prev) => (prev === undefined ? user : prev)));
  }, [oauthParams]);

  useEffect(() => {
    if (!oauthParams || !isAllowedOAuthCallback(oauthParams.oauthCallback)) return;
    let live = true;
    lookupDestination(oauthParams.oauthCallback, oauthParams.clientId, oauthParams.redirectUri).then((a) => live && setAnswer(a));
    return () => {
      live = false;
    };
  }, [oauthParams]);

  /** A known host connects straight after sign-in; any other host shows its warning first. */
  const afterSignIn = async (user: User) => {
    if (!oauthParams) return onSuccess?.();
    if (destination?.known) return handleOAuthCallback(oauthParams);
    setConfirmed(false);
    setSignedIn(user);
  };

  function switchMode(next: AuthMode) {
    setMode(next);
    setError(null);
    setNotice(null);
    setConfirmPassword('');
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);

    // Password reset — no password needed.
    if (mode === 'reset') {
      if (!email) { setError('Enter your email'); return; }
      setLoading(true);
      try {
        await sendPasswordReset(email, await projectForEmail(email));
        setNotice('If an account exists for that email, a password-reset link is on its way.');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not send reset email');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    if (mode === 'signup') {
      if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
      if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    }

    setLoading(true);
    try {
      if (mode === 'signup') {
        await signUpWithEmailPassword(email, password, await projectForSignUp());
        // Account created (unverified) + verification email sent. In OAuth mode
        // we can't authorize an unverified user, so route them to verify first.
        if (oauthParams) {
          setNotice('Account created. Check your inbox to verify your email, then sign in to continue.');
          switchMode('signin');
          return;
        }
        onSuccess?.(); // app shell shows the verify-email gate next
        return;
      }

      await afterSignIn(await signInWithEmailPassword(email, password, await projectForEmail(email)));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError(null);

    try {
      await afterSignIn(await signInWithGoogleOnAnyProject());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Google sign-in failed';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setGoogleLoading(false);
    }
  };

  const fail = (err: unknown, fallback: string) => {
    const errorMessage = err instanceof Error ? err.message : fallback;
    setError(errorMessage);
    onError?.(errorMessage);
  };

  const handleContinue = async () => {
    if (!destination?.known && !confirmed) return;
    setLoading(true);
    setError(null);
    try {
      await handleOAuthCallback(oauthParams!);
    } catch (err) {
      fail(err, 'Authorization failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUseAnotherAccount = async () => {
    setLoading(true);
    setError(null);
    try {
      await signOutUser();
      setSignedIn(null);
    } catch (err) {
      fail(err, 'Could not sign out');
    } finally {
      setLoading(false);
    }
  };

  if (isOAuthMode && !isAllowedOAuthCallback(oauthParams!.oauthCallback)) {
    return (
      <Card>
        <CardContent className="py-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{FOREIGN_CALLBACK}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (isOAuthMode && (signedIn === undefined || answer === undefined)) {
    return (
      <Card>
        <CardContent className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Checking your sign-in" />
        </CardContent>
      </Card>
    );
  }

  if (isOAuthMode && answer?.refused) {
    return (
      <Card>
        <CardContent className="py-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This sign-in link can’t be used ({answer.message}), so nothing was sent. Start the connection again from Claude.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (isOAuthMode && signedIn && destination && !destination.known) {
    const who = signedIn.email ?? 'this account';
    const host = destination.redirectHost;
    const app = destination.clientName ? `An app calling itself “${destination.clientName}”` : 'An app';
    return (
      <Card className="border-amber-500/60">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Check where this sign-in goes</CardTitle>
          <CardDescription>
            Signed in as <span className="break-all">{who}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div role="alert" className="rounded-md border border-amber-500/60 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <p>
              {app} wants access to your 4K Labs account. Your sign-in will go to <strong className="break-all">{host}</strong>.
            </p>
            <p className="mt-2">If you didn’t start this from {host} yourself, close this page.</p>
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="flex items-center gap-2">
            <Checkbox id="oauth-confirm-host" checked={confirmed} onCheckedChange={(v) => setConfirmed(v === true)} disabled={isLoading} />
            <Label htmlFor="oauth-confirm-host" className="break-all">I started this from {host}</Label>
          </div>
          <Button className="w-full" onClick={handleContinue} disabled={isLoading || !confirmed}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue to {host}
          </Button>
          <Button variant="outline" className="w-full" onClick={handleUseAnotherAccount} disabled={isLoading}>
            Use another account
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (isOAuthMode && signedIn && destination) {
    const who = signedIn.email ?? 'this account';
    const app = destination.clientName ?? destination.redirectHost;
    return (
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Connect 4K Labs to {app} as <span className="break-all">{who}</span></CardTitle>
          <CardDescription>{app} will make videos and spend credits on this account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-sm text-muted-foreground">
            You’ll go back to <strong className="break-all">{destination.redirectHost}</strong>
          </p>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Button className="w-full" onClick={handleContinue} disabled={isLoading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue as {who}
          </Button>
          <Button variant="outline" className="w-full" onClick={handleUseAnotherAccount} disabled={isLoading}>
            Use another account
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle className="text-2xl">
          {mode === 'signup' ? 'Create your account' : mode === 'reset' ? 'Reset your password' : 'Sign in'}
        </CardTitle>
        <CardDescription>
          {mode === 'reset'
            ? 'We’ll email you a link to set a new password.'
            : destination?.known
            ? `Sign in to authorize ${destination.clientName ?? destination.redirectHost}`
            : isOAuthMode
            ? 'Sign in, then confirm where this sign-in goes'
            : mode === 'signup'
            ? 'Start making videos in minutes.'
            : 'Enter your credentials to continue'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Notice (reset sent, etc.) */}
        {notice && (
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>{notice}</AlertDescription>
          </Alert>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {mode !== 'reset' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === 'signin' && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => switchMode('reset')}
                    disabled={isLoading}
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={mode === 'signup' ? 'At least 6 characters' : 'Enter your password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className="pr-10"
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>
          )}

          {mode === 'signup' && (
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {mode === 'signup' ? 'Creating account...' : mode === 'reset' ? 'Sending...' : 'Signing in...'}
              </>
            ) : mode === 'signup' ? (
              'Create account'
            ) : mode === 'reset' ? (
              'Send reset link'
            ) : (
              'Sign in'
            )}
          </Button>
        </form>

        {mode !== 'reset' && (
        <>
        {/* Separator */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">or continue with</span>
          </div>
        </div>

        {/* Google Button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={handleGoogleLogin}
          disabled={isLoading}
        >
          {googleLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          )}
          Google
        </Button>
        </>
        )}

        {/* Mode toggle footer */}
        <div className="text-center text-sm text-muted-foreground">
          {mode === 'signin' && (
            <>
              Don’t have an account?{' '}
              <button
                type="button"
                className="font-medium text-foreground hover:underline"
                onClick={() => switchMode('signup')}
                disabled={isLoading}
              >
                Sign up
              </button>
            </>
          )}
          {mode === 'signup' && (
            <>
              Already have an account?{' '}
              <button
                type="button"
                className="font-medium text-foreground hover:underline"
                onClick={() => switchMode('signin')}
                disabled={isLoading}
              >
                Sign in
              </button>
            </>
          )}
          {mode === 'reset' && (
            <button
              type="button"
              className="font-medium text-foreground hover:underline"
              onClick={() => switchMode('signin')}
              disabled={isLoading}
            >
              Back to sign in
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
